import React, { useMemo } from 'react';
import {
  buildInventoryGuideSections,
  inventoryGuideItemStatus,
  inventoryGuideProgress,
  inventoryGuideTitle,
  nextInventoryGuideItem,
} from './inventoryBuildGuideModel';
import './inventory-guide.css';

export default function InventoryBuildGuide({
  targetId,
  catalog,
  reconciliation,
  expirations,
  selectedCatalogId,
  onSelect,
}) {
  const sections = useMemo(
    () => buildInventoryGuideSections(catalog, reconciliation, targetId, expirations),
    [catalog, reconciliation, targetId, expirations],
  );

  if (!sections.length) return null;

  const progress = inventoryGuideProgress(sections);
  const nextItem = nextInventoryGuideItem(sections, selectedCatalogId);
  const title = inventoryGuideTitle(targetId);

  const selectItem = item => {
    if (!item) return;
    onSelect?.(item);
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        document.getElementById('inventory-item-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  };

  return (
    <section className={`inventory-card inventory-guide${progress.ready ? ' ready' : ''}`}>
      <div className="inventory-guide-head">
        <div>
          <small>2. GUIDED BUILD</small>
          <h2>{title}</h2>
          <p>Work through the container in its physical build order. Scan an item at any time, or click a row to open it below.</p>
        </div>
        <div className="inventory-guide-progress">
          <strong>{progress.complete}/{progress.total}</strong>
          <span>{progress.ready ? 'COMPLETE' : `${progress.remaining} remaining`}</span>
        </div>
      </div>

      {nextItem && (
        <button type="button" className="inventory-guide-next" onClick={() => selectItem(nextItem)}>
          {selectedCatalogId ? `Next incomplete: ${nextItem.item}` : `Start with ${nextItem.item}`}
        </button>
      )}

      <div className="inventory-guide-sections">
        {sections.map(section => (
          <div className="inventory-guide-section" key={section.id}>
            <div className="inventory-guide-section-head">
              <h3>{section.component}</h3>
              <span>{section.complete}/{section.total} complete</span>
            </div>
            <div className="inventory-guide-rows">
              {section.items.map(item => {
                const selected = String(item.catalogId) === String(selectedCatalogId || '');
                const status = inventoryGuideItemStatus(item);
                const statusClass = item.expired || item.needsReview
                  ? 'problem'
                  : item.complete
                    ? 'complete'
                    : item.found > 0
                      ? 'partial'
                      : 'missing';
                return (
                  <button
                    type="button"
                    key={item.catalogId}
                    className={`inventory-guide-row ${statusClass}${selected ? ' selected' : ''}`}
                    onClick={() => selectItem(item)}
                  >
                    <span className="inventory-guide-row-name">
                      <strong>{item.item}</strong>
                      <small>{item.trackingMode === 'counted' ? 'Counted / bulk' : 'Individual unit'} · {item.catalogId}</small>
                    </span>
                    <span className="inventory-guide-row-count">
                      <strong>{item.found}/{item.requiredLabel}</strong>
                      <small>{status}</small>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
